/**
 * Check-in API. Document check-in HTTP calls.
 * Used by checkinApiSlice; uses ENDPOINTS and getApiClient (not documentsAPI).
 */
import { getApiClient } from "./client";
import { ENDPOINTS } from "../configs/endpoints";
import { logger } from "../../utils/logger";

/** Default axios client timeout (60s) is too low for sequential bulk check-in. */
const BULK_CHECKIN_TIMEOUT_BASE_MS = 120_000;
const BULK_CHECKIN_TIMEOUT_PER_DOC_MS = 90_000;
const BULK_CHECKIN_TIMEOUT_MAX_MS = 480_000;

export function getBulkCheckinTimeoutMs(documentCount) {
  const count = Math.max(1, Number(documentCount) || 1);
  return Math.min(
    BULK_CHECKIN_TIMEOUT_MAX_MS,
    BULK_CHECKIN_TIMEOUT_BASE_MS + count * BULK_CHECKIN_TIMEOUT_PER_DOC_MS,
  );
}

export const checkinAPI = {
  /**
   * Check-in a document (single file).
   * @param {string} accessToken - Bearer token from UserAuth.getUserToken()
   * @param {object} body - Request body for check-in
   * @param {boolean} [body.checkinViewCopy]
   * @param {string} body.docClass
   * @param {string} body.title
   * @param {string} body.docFormat
   * @param {string} body.fileData - base64 (no data URL prefix)
   * @param {string} body.fileName
   * @param {Array<{ luName, keyRef, updateRevision?, updateRevisionCode? }>} body.objectConnections
   * @param {boolean} body.createViewCopy
   * @param {boolean} body.rollbackRevisionOnFail
   * @param {string} body.webUser
   * @param {string} body.status
   * @returns {Promise<{ data: any, status: number }>}
   */
  async checkinDocument(accessToken, body) {
    logger.info("checkinAPI.checkinDocument", { docClass: body?.docClass, title: body?.title }, "API");
    try {
      const client = getApiClient(accessToken);
      const url = ENDPOINTS.CHECKIN.DOCUMENT();
      const response = await client.post(url, body);
      return { data: response.data, status: response.status };
    } catch (err) {
      logger.error("checkinAPI.checkinDocument failed", err, "API");
      throw err;
    }
  },

  /**
   * Check in multiple documents in one request.
   * @param {string} accessToken
   * @param {object[]} body - Array of DocInfoModel payloads
   * @returns {Promise<{ data: any, status: number }>}
   */
  async checkinDocumentsBulk(accessToken, body) {
    logger.info(
      "checkinAPI.checkinDocumentsBulk",
      { count: Array.isArray(body) ? body.length : 0 },
      "API",
    );
    try {
      const client = getApiClient(accessToken);
      const url = ENDPOINTS.CHECKIN.DOCUMENTS_BULK();
      const timeout = getBulkCheckinTimeoutMs(
        Array.isArray(body) ? body.length : 0,
      );
      const response = await client.post(url, body, { timeout });
      return { data: response.data, status: response.status };
    } catch (err) {
      logger.error("checkinAPI.checkinDocumentsBulk failed", err, "API");
      throw err;
    }
  },
};
